import React, { useEffect, useMemo, useRef, useState } from "react";
import { API_BASE } from "../config/api";

const GestionDetallesTecnico = () => {
  const tecnicoId = useMemo(() => {
    return (
      localStorage.getItem("adminId") ||
      localStorage.getItem("administradorId") ||
      localStorage.getItem("tecnicoId") ||
      ""
    );
  }, []);

  const [salasPermitidas, setSalasPermitidas] = useState([]);
  const [salaSeleccionada, setSalaSeleccionada] = useState("");

  const [equipos, setEquipos] = useState([]);
  const [equipoSeleccionado, setEquipoSeleccionado] = useState("");

  // Detalle sala
  const [detalleSala, setDetalleSala] = useState(null);

  // Detalle equipo
  const [detalleEquipo, setDetalleEquipo] = useState(null);

  const [cargando, setCargando] = useState(false);
  const [cargandoPermisos, setCargandoPermisos] = useState(true);

  // Sub-pestañas dentro de "Detalles"
  const [vistaDetalles, setVistaDetalles] = useState("sala"); // 'sala' | 'equipos'

  // ✅ “toque fino”: foco al selector de equipo cuando entro a pestaña Equipos
  const selectEquipoRef = useRef(null);

  // --- helpers ---
  const siNo = (v) => (Number(v) === 1 ? 1 : 0);

  // Cargar salas permitidas
  useEffect(() => {
    if (!tecnicoId) {
      setCargandoPermisos(false);
      setSalasPermitidas([]);
      return;
    }

    setCargandoPermisos(true);

    fetch(`${API_BASE}/api/tecnicos/${tecnicoId}/salas-permitidas`)
      .then((res) => (res.ok ? res.json() : Promise.resolve([])))
      .then((data) => setSalasPermitidas(Array.isArray(data) ? data : []))
      .catch(() => setSalasPermitidas([]))
      .finally(() => setCargandoPermisos(false));
  }, [tecnicoId]);

  // Cuando elige sala: cargar detalle sala + equipos
  useEffect(() => {
    if (!salaSeleccionada) {
      setDetalleSala(null);
      setEquipos([]);
      setEquipoSeleccionado("");
      setDetalleEquipo(null);
      setVistaDetalles("sala");
      return;
    }

    setCargando(true);

    Promise.all([
      fetch(`${API_BASE}/api/salas/${salaSeleccionada}/detalle`).then((r) =>
        r.ok ? r.json() : Promise.resolve(null)
      ),
      fetch(`${API_BASE}/api/salas/${salaSeleccionada}/equipos`).then((r) =>
        r.ok ? r.json() : Promise.resolve([])
      ),
    ])
      .then(([det, eqs]) => {
        setDetalleSala(
          det || {
            tiene_impresora: 0,
            impresora_marca: "",
            impresora_modelo: "",
            impresora_tiene_cable_usb: 0,

            tiene_proyector: 0,
            proyector_marca: "",
            proyector_modelo: "",
            proyector_cable_tipo: "NINGUNO",

            tiene_televisor: 0,
            televisor_marca: "",
            televisor_pulgadas: "",
            televisor_cable_tipo: "NINGUNO",
            televisor_tiene_control: 0,

            observacion: "",
          }
        );

        setEquipos(Array.isArray(eqs) ? eqs : []);

        // al cambiar de sala, arrancar en "Detalle de Sala"
        setVistaDetalles("sala");
        setEquipoSeleccionado("");
        setDetalleEquipo(null);
      })
      .catch(() => {
        setDetalleSala(null);
        setEquipos([]);
      })
      .finally(() => setCargando(false));
  }, [salaSeleccionada]);

  // Cuando elige equipo: cargar detalle equipo
  useEffect(() => {
    if (!equipoSeleccionado) {
      setDetalleEquipo(null);
      return;
    }

    setCargando(true);

    fetch(`${API_BASE}/api/equipos/${equipoSeleccionado}/detalle`)
      .then((r) => (r.ok ? r.json() : Promise.resolve(null)))
      .then((det) => {
        // cable_video_tipo en BD: VGA/HDMI/NINGUNO -> UI: VGA/HDMI/NO
        const cableBd = (det?.cable_video_tipo || "NINGUNO").toString().trim().toUpperCase();
        const cableUi = cableBd === "VGA" || cableBd === "HDMI" ? cableBd : "NO";

        // cable_power_tipo en BD: AMERICANO/BRASILERO/SCHUKO/SIN_POWER
        const powerBd = (det?.cable_power_tipo || "SIN_POWER").toString().trim().toUpperCase();
        const powerUi =
          powerBd === "AMERICANO" ||
          powerBd === "BRASILERO" ||
          powerBd === "SCHUKO" ||
          powerBd === "SIN_POWER"
            ? powerBd
            : "SIN_POWER";

        setDetalleEquipo(
          det
            ? {
                placa_madre_modelo: det.placa_madre_modelo || "",
                procesador_modelo: det.procesador_modelo || "",
                ram_gb: det.ram_gb ?? "",

                disco_tipo: det.disco_tipo || "HDD",
                disco_gb: det.disco_gb ?? "",

                cable_power_tipo: powerUi,
                cable_video_tipo_ui: cableUi,

                tiene_teclado: det.tiene_teclado ? 1 : 0,
                teclado_detalle: det.teclado_detalle || "",

                tiene_mouse: det.tiene_mouse ? 1 : 0,
                mouse_detalle: det.mouse_detalle || "",

                monitor_marca: det.monitor_marca || "",
                observacion: det.observacion || "",
              }
            : {
                placa_madre_modelo: "",
                procesador_modelo: "",
                ram_gb: "",

                disco_tipo: "HDD",
                disco_gb: "",

                cable_power_tipo: "SIN_POWER",
                cable_video_tipo_ui: "NO",

                tiene_teclado: 0,
                teclado_detalle: "",

                tiene_mouse: 0,
                mouse_detalle: "",

                monitor_marca: "",
                observacion: "",
              }
        );
      })
      .catch(() => setDetalleEquipo(null))
      .finally(() => setCargando(false));
  }, [equipoSeleccionado]);

  // ✅ Toque fino: al entrar en "Detalle de Equipos", enfocar selector y mostrar aviso si no hay equipo elegido
  useEffect(() => {
    if (vistaDetalles !== "equipos") return;
    // esperar a que pinte el DOM
    const t = setTimeout(() => {
      if (selectEquipoRef.current) selectEquipoRef.current.focus();
    }, 50);
    return () => clearTimeout(t);
  }, [vistaDetalles, salaSeleccionada]);

  const guardarDetalleSala = async () => {
    if (!salaSeleccionada || !detalleSala) return;

    const body = {
      // Impresora
      tiene_impresora: siNo(detalleSala.tiene_impresora),
      impresora_marca: detalleSala.impresora_marca || null,
      impresora_modelo: detalleSala.impresora_modelo || null,
      impresora_tiene_cable_usb: siNo(detalleSala.impresora_tiene_cable_usb),

      // Proyector
      tiene_proyector: siNo(detalleSala.tiene_proyector),
      proyector_marca: detalleSala.proyector_marca || null,
      proyector_modelo: detalleSala.proyector_modelo || null,
      proyector_cable_tipo: detalleSala.proyector_cable_tipo || "NINGUNO",

      // Televisor
      tiene_televisor: siNo(detalleSala.tiene_televisor),
      televisor_marca: detalleSala.televisor_marca || null,
      televisor_pulgadas: detalleSala.televisor_pulgadas === "" ? null : detalleSala.televisor_pulgadas,
      televisor_cable_tipo: detalleSala.televisor_cable_tipo || "NINGUNO",
      televisor_tiene_control: siNo(detalleSala.televisor_tiene_control),

      observacion: detalleSala.observacion || null,

      actorRol: "tecnico",
      tecnico_id: tecnicoId,
    };

    try {
      const resp = await fetch(`${API_BASE}/api/salas/${salaSeleccionada}/detalle`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      if (resp.ok) {
        alert("✅ Detalle de sala guardado");
      } else {
        let msg = "❌ No se pudo guardar el detalle de sala";
        try {
          const data = await resp.json();
          if (data?.mensaje) msg = `❌ ${data.mensaje}`;
        } catch {}
        alert(msg);
      }
    } catch (e) {
      console.error("Error guardando detalle sala:", e);
      alert("❌ Error de conexión al guardar detalle de sala");
    }
  };

  const guardarDetalleEquipo = async () => {
    if (!equipoSeleccionado || !detalleEquipo) return;

    // cable video UI -> BD
    const uiCable = (detalleEquipo.cable_video_tipo_ui || "NO").toString().trim().toUpperCase();
    const bdCable = uiCable === "NO" ? "NINGUNO" : uiCable; // VGA/HDMI o NINGUNO
    const tieneCableVideo = bdCable === "VGA" || bdCable === "HDMI" ? 1 : 0;

    // cable power tipo: validación suave
    const power = (detalleEquipo.cable_power_tipo || "SIN_POWER").toString().trim().toUpperCase();
    const powerFinal =
      power === "AMERICANO" || power === "BRASILERO" || power === "SCHUKO" || power === "SIN_POWER"
        ? power
        : "SIN_POWER";

    const body = {
      placa_madre_modelo: detalleEquipo.placa_madre_modelo || null,
      procesador_modelo: detalleEquipo.procesador_modelo || null,
      ram_gb: detalleEquipo.ram_gb === "" ? null : detalleEquipo.ram_gb,

      disco_tipo: detalleEquipo.disco_tipo || "HDD",
      disco_gb: detalleEquipo.disco_gb === "" ? null : detalleEquipo.disco_gb,

      cable_power_tipo: powerFinal,

      // compatibilidad backend (aún existe tiene_cable_video)
      tiene_cable_video: tieneCableVideo,
      cable_video_tipo: bdCable,

      tiene_teclado: siNo(detalleEquipo.tiene_teclado),
      teclado_detalle: siNo(detalleEquipo.tiene_teclado) ? (detalleEquipo.teclado_detalle || null) : null,

      tiene_mouse: siNo(detalleEquipo.tiene_mouse),
      mouse_detalle: siNo(detalleEquipo.tiene_mouse) ? (detalleEquipo.mouse_detalle || null) : null,

      monitor_marca: detalleEquipo.monitor_marca || null,
      monitor_pulgadas: null, // ya no se usa

      observacion: detalleEquipo.observacion || null,

      actorRol: "tecnico",
      tecnico_id: tecnicoId,
      sala_id: salaSeleccionada,
    };

    try {
      const resp = await fetch(`${API_BASE}/api/equipos/${equipoSeleccionado}/detalle`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      if (resp.ok) {
        alert("✅ Detalle de equipo guardado");
      } else {
        let msg = "❌ No se pudo guardar el detalle de equipo";
        try {
          const data = await resp.json();
          if (data?.mensaje) msg = `❌ ${data.mensaje}`;
        } catch {}
        alert(msg);
      }
    } catch (e) {
      console.error("Error guardando detalle equipo:", e);
      alert("❌ Error de conexión al guardar detalle de equipo");
    }
  };

  // Si no se identifica técnico
  if (!tecnicoId) {
    return (
      <div className="p-6">
        <h2 className="text-xl font-bold text-red-700 mb-2">Necesitas permiso del Administrador</h2>
        <p className="text-gray-700">No se pudo identificar al técnico logueado.</p>
      </div>
    );
  }

  // NO mostrar mensaje rojo hasta terminar de cargar permisos
  if (cargandoPermisos) {
    return (
      <div className="p-6">
        <p className="text-gray-600">Cargando permisos...</p>
      </div>
    );
  }

  // Ya cargó, si no hay salas permitidas recién ahí mostrar mensaje
  if (salasPermitidas.length === 0) {
    return (
      <div className="p-6">
        <h2 className="text-xl font-bold text-red-700 mb-2">Necesitas permiso del Administrador</h2>
        <p className="text-gray-700">Aún no tenés salas autorizadas para editar detalles.</p>
      </div>
    );
  }

  const TabButton = ({ active, onClick, children }) => (
    <button
      onClick={onClick}
      className={
        active
          ? "px-4 py-2 rounded bg-blue-600 text-white"
          : "px-4 py-2 rounded border hover:bg-gray-50"
      }
      type="button"
    >
      {children}
    </button>
  );

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold text-blue-700 mb-4">Gestión de Detalles (Técnico)</h2>

      {/* Selector de sala */}
      <div className="bg-white shadow p-4 rounded mb-6 space-y-4">
        <label className="block text-sm font-semibold text-gray-700">Sala autorizada</label>
        <select
          value={salaSeleccionada}
          onChange={(e) => setSalaSeleccionada(e.target.value)}
          className="w-full border px-3 py-2 rounded"
        >
          <option value="">-- Seleccionar sala --</option>
          {salasPermitidas.map((s) => (
            <option key={s.id} value={s.id}>
              {s.colegio} - {s.nombre} ({s.tipo})
            </option>
          ))}
        </select>

        {cargando && <p className="text-gray-600">Cargando...</p>}
      </div>

      {/* Sub-pestañas SOLO cuando ya hay sala */}
      {salaSeleccionada && (
        <div className="flex gap-2 mb-4">
          <TabButton active={vistaDetalles === "sala"} onClick={() => setVistaDetalles("sala")}>
            Detalle de Sala
          </TabButton>
          <TabButton active={vistaDetalles === "equipos"} onClick={() => setVistaDetalles("equipos")}>
            Detalle de Equipos
          </TabButton>
        </div>
      )}

      {/* ✅ Caja contenedora UNIFICADA (mismo ancho para Sala y Equipos) */}
      {salaSeleccionada && (
        <div className="bg-white shadow p-4 rounded mb-6">
          {/* ===================== */}
          {/* DETALLE SALA (TAB) */}
          {/* ===================== */}
          {vistaDetalles === "sala" && detalleSala && (
            <div className="space-y-4">
              <h3 className="text-lg font-bold text-gray-800">Detalle de Sala</h3>

              {/* Impresora */}
              <div className="border rounded p-3 space-y-3">
                <h4 className="font-semibold text-gray-700">Impresora</h4>

                <select
                  value={detalleSala.tiene_impresora}
                  onChange={(e) => setDetalleSala({ ...detalleSala, tiene_impresora: Number(e.target.value) })}
                  className="w-full border px-3 py-2 rounded"
                >
                  <option value={0}>No tiene impresora</option>
                  <option value={1}>Sí tiene impresora</option>
                </select>

                {Number(detalleSala.tiene_impresora) === 1 && (
                  <>
                    <input
                      value={detalleSala.impresora_marca || ""}
                      onChange={(e) => setDetalleSala({ ...detalleSala, impresora_marca: e.target.value })}
                      placeholder="Marca"
                      className="w-full border px-3 py-2 rounded"
                    />
                    <input
                      value={detalleSala.impresora_modelo || ""}
                      onChange={(e) => setDetalleSala({ ...detalleSala, impresora_modelo: e.target.value })}
                      placeholder="Modelo"
                      className="w-full border px-3 py-2 rounded"
                    />
                    <select
                      value={detalleSala.impresora_tiene_cable_usb}
                      onChange={(e) =>
                        setDetalleSala({ ...detalleSala, impresora_tiene_cable_usb: Number(e.target.value) })
                      }
                      className="w-full border px-3 py-2 rounded"
                    >
                      <option value={0}>Sin cable USB</option>
                      <option value={1}>Con cable USB</option>
                    </select>
                  </>
                )}
              </div>

              {/* Proyector */}
              <div className="border rounded p-3 space-y-3">
                <h4 className="font-semibold text-gray-700">Proyector</h4>

                <select
                  value={detalleSala.tiene_proyector}
                  onChange={(e) => setDetalleSala({ ...detalleSala, tiene_proyector: Number(e.target.value) })}
                  className="w-full border px-3 py-2 rounded"
                >
                  <option value={0}>No tiene proyector</option>
                  <option value={1}>Sí tiene proyector</option>
                </select>

                {Number(detalleSala.tiene_proyector) === 1 && (
                  <>
                    <input
                      value={detalleSala.proyector_marca || ""}
                      onChange={(e) => setDetalleSala({ ...detalleSala, proyector_marca: e.target.value })}
                      placeholder="Marca"
                      className="w-full border px-3 py-2 rounded"
                    />
                    <input
                      value={detalleSala.proyector_modelo || ""}
                      onChange={(e) => setDetalleSala({ ...detalleSala, proyector_modelo: e.target.value })}
                      placeholder="Modelo"
                      className="w-full border px-3 py-2 rounded"
                    />
                    <select
                      value={detalleSala.proyector_cable_tipo || "NINGUNO"}
                      onChange={(e) => setDetalleSala({ ...detalleSala, proyector_cable_tipo: e.target.value })}
                      className="w-full border px-3 py-2 rounded"
                    >
                      <option value="VGA">VGA</option>
                      <option value="HDMI">HDMI</option>
                      <option value="NINGUNO">NINGUNO</option>
                    </select>
                  </>
                )}
              </div>

              {/* Televisor */}
              <div className="border rounded p-3 space-y-3">
                <h4 className="font-semibold text-gray-700">Televisor</h4>

                <select
                  value={detalleSala.tiene_televisor}
                  onChange={(e) => setDetalleSala({ ...detalleSala, tiene_televisor: Number(e.target.value) })}
                  className="w-full border px-3 py-2 rounded"
                >
                  <option value={0}>No tiene televisor</option>
                  <option value={1}>Sí tiene televisor</option>
                </select>

                {Number(detalleSala.tiene_televisor) === 1 && (
                  <>
                    <input
                      value={detalleSala.televisor_marca || ""}
                      onChange={(e) => setDetalleSala({ ...detalleSala, televisor_marca: e.target.value })}
                      placeholder="Marca"
                      className="w-full border px-3 py-2 rounded"
                    />
                    <input
                      type="number"
                      min="0"
                      value={detalleSala.televisor_pulgadas || ""}
                      onChange={(e) => setDetalleSala({ ...detalleSala, televisor_pulgadas: e.target.value })}
                      placeholder="Pulgadas"
                      className="w-full border px-3 py-2 rounded"
                    />
                    <select
                      value={detalleSala.televisor_cable_tipo || "NINGUNO"}
                      onChange={(e) => setDetalleSala({ ...detalleSala, televisor_cable_tipo: e.target.value })}
                      className="w-full border px-3 py-2 rounded"
                    >
                      <option value="VGA">VGA</option>
                      <option value="HDMI">HDMI</option>
                      <option value="NINGUNO">NINGUNO</option>
                    </select>

                    <select
                      value={detalleSala.televisor_tiene_control}
                      onChange={(e) =>
                        setDetalleSala({ ...detalleSala, televisor_tiene_control: Number(e.target.value) })
                      }
                      className="w-full border px-3 py-2 rounded"
                    >
                      <option value={0}>Sin control</option>
                      <option value={1}>Con control</option>
                    </select>
                  </>
                )}
              </div>

              <textarea
                value={detalleSala.observacion || ""}
                onChange={(e) => setDetalleSala({ ...detalleSala, observacion: e.target.value })}
                placeholder="Observación (opcional)"
                className="w-full border px-3 py-2 rounded"
                rows={3}
              />

              <button
                onClick={guardarDetalleSala}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
              >
                Guardar detalle de sala
              </button>
            </div>
          )}

          {/* ===================== */}
          {/* DETALLE EQUIPOS (TAB) */}
          {/* ===================== */}
          {vistaDetalles === "equipos" && (
            <div className="space-y-4">
              <h3 className="text-lg font-bold text-gray-800">Detalle de Equipos</h3>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Equipo</label>
                <select
                  ref={selectEquipoRef}
                  value={equipoSeleccionado}
                  onChange={(e) => setEquipoSeleccionado(e.target.value)}
                  className="w-full border px-3 py-2 rounded"
                >
                  <option value="">-- Seleccionar equipo --</option>
                  {equipos.map((eq) => (
                    <option key={eq.id} value={eq.id}>
                      {eq.nombre}
                    </option>
                  ))}
                </select>
                {!equipoSeleccionado && (
                  <p className="text-xs text-gray-500 mt-1">
                    Seleccioná un equipo para cargar o editar su ficha técnica.
                  </p>
                )}
              </div>

              {equipoSeleccionado && detalleEquipo && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Placa madre (modelo)</label>
                      <input
                        value={detalleEquipo.placa_madre_modelo || ""}
                        onChange={(e) =>
                          setDetalleEquipo({ ...detalleEquipo, placa_madre_modelo: e.target.value })
                        }
                        className="w-full border px-3 py-2 rounded"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Procesador (modelo)</label>
                      <input
                        value={detalleEquipo.procesador_modelo || ""}
                        onChange={(e) =>
                          setDetalleEquipo({ ...detalleEquipo, procesador_modelo: e.target.value })
                        }
                        className="w-full border px-3 py-2 rounded"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">RAM (GB)</label>
                      <input
                        type="number"
                        min="0"
                        value={detalleEquipo.ram_gb || ""}
                        onChange={(e) => setDetalleEquipo({ ...detalleEquipo, ram_gb: e.target.value })}
                        className="w-full border px-3 py-2 rounded"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Disco (tipo)</label>
                      <select
                        value={detalleEquipo.disco_tipo || "HDD"}
                        onChange={(e) => setDetalleEquipo({ ...detalleEquipo, disco_tipo: e.target.value })}
                        className="w-full border px-3 py-2 rounded"
                      >
                        <option value="HDD">HDD</option>
                        <option value="SSD">SSD</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Disco (GB)</label>
                      <input
                        type="number"
                        min="0"
                        value={detalleEquipo.disco_gb || ""}
                        onChange={(e) => setDetalleEquipo({ ...detalleEquipo, disco_gb: e.target.value })}
                        className="w-full border px-3 py-2 rounded"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Cable power</label>
                      <select
                        value={detalleEquipo.cable_power_tipo || "SIN_POWER"}
                        onChange={(e) => setDetalleEquipo({ ...detalleEquipo, cable_power_tipo: e.target.value })}
                        className="w-full border px-3 py-2 rounded"
                      >
                        <option value="AMERICANO">Americano</option>
                        <option value="BRASILERO">Brasilero</option>
                        <option value="SCHUKO">Schuko</option>
                        <option value="SIN_POWER">Sin power</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Cable de video</label>
                      <select
                        value={detalleEquipo.cable_video_tipo_ui || "NO"}
                        onChange={(e) =>
                          setDetalleEquipo({ ...detalleEquipo, cable_video_tipo_ui: e.target.value })
                        }
                        className="w-full border px-3 py-2 rounded"
                      >
                        <option value="NO">NO</option>
                        <option value="VGA">VGA</option>
                        <option value="HDMI">HDMI</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Teclado</label>
                      <select
                        value={detalleEquipo.tiene_teclado}
                        onChange={(e) => {
                          const v = Number(e.target.value);
                          setDetalleEquipo({
                            ...detalleEquipo,
                            tiene_teclado: v,
                            teclado_detalle: v === 0 ? "" : detalleEquipo.teclado_detalle,
                          });
                        }}
                        className="w-full border px-3 py-2 rounded"
                      >
                        <option value={0}>Sin teclado</option>
                        <option value={1}>Con teclado</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Teclado (detalle)</label>
                      <input
                        value={Number(detalleEquipo.tiene_teclado) === 1 ? (detalleEquipo.teclado_detalle || "") : ""}
                        onChange={(e) => setDetalleEquipo({ ...detalleEquipo, teclado_detalle: e.target.value })}
                        placeholder="Marca y serie"
                        className="w-full border px-3 py-2 rounded"
                        disabled={Number(detalleEquipo.tiene_teclado) !== 1}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Mouse</label>
                      <select
                        value={detalleEquipo.tiene_mouse}
                        onChange={(e) => {
                          const v = Number(e.target.value);
                          setDetalleEquipo({
                            ...detalleEquipo,
                            tiene_mouse: v,
                            mouse_detalle: v === 0 ? "" : detalleEquipo.mouse_detalle,
                          });
                        }}
                        className="w-full border px-3 py-2 rounded"
                      >
                        <option value={0}>Sin mouse</option>
                        <option value={1}>Con mouse</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Mouse (detalle)</label>
                      <input
                        value={Number(detalleEquipo.tiene_mouse) === 1 ? (detalleEquipo.mouse_detalle || "") : ""}
                        onChange={(e) => setDetalleEquipo({ ...detalleEquipo, mouse_detalle: e.target.value })}
                        placeholder="Marca y serie"
                        className="w-full border px-3 py-2 rounded"
                        disabled={Number(detalleEquipo.tiene_mouse) !== 1}
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Monitor (marca)</label>
                      <input
                        value={detalleEquipo.monitor_marca || ""}
                        onChange={(e) => setDetalleEquipo({ ...detalleEquipo, monitor_marca: e.target.value })}
                        className="w-full border px-3 py-2 rounded"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Observación</label>
                    <textarea
                      value={detalleEquipo.observacion || ""}
                      onChange={(e) => setDetalleEquipo({ ...detalleEquipo, observacion: e.target.value })}
                      className="w-full border px-3 py-2 rounded"
                      rows={3}
                    />
                  </div>

                  <button
                    onClick={guardarDetalleEquipo}
                    className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                  >
                    Guardar detalle de equipo
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GestionDetallesTecnico;